import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import { Save, Eye, Trash2, Edit2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface SavedView {
  id: string;
  name: string;
  description?: string;
  filters: any;
  sort_by?: string;
  sort_order?: string;
  created_at: string;
}

interface SavedViewsManagerProps {
  currentFilters: any;
  currentSort: { sortBy: string; sortOrder: string };
  onApplyView: (view: SavedView) => void;
}

export default function SavedViewsManager({ currentFilters, currentSort, onApplyView }: SavedViewsManagerProps) {
  const { getToken } = useAuth();
  const [views, setViews] = useState<SavedView[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadViews();
  }, []);

  const loadViews = async () => {
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/v1/dashboard/views`, { headers });
      setViews(response.data);
    } catch (error) {
      console.error('Failed to load saved views:', error);
    }
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) {
      setValidationError('Please enter a name for this view');
      return;
    }

    try {
      setLoading(true);
      setValidationError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${BACKEND_URL}/api/v1/dashboard/views`,
        {
          name: viewName,
          description: viewDescription,
          filters: currentFilters,
          sort_by: currentSort.sortBy,
          sort_order: currentSort.sortOrder
        },
        { headers }
      );

      setSuccessMessage('View saved successfully!');
      setTimeout(() => {
        setShowSaveModal(false);
        setSuccessMessage(null);
      }, 2000);
      setViewName('');
      setViewDescription('');
      loadViews();
    } catch (error) {
      console.error('Failed to save view:', error);
      setValidationError('Failed to save view');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteView = async (viewId: string) => {
    if (!window.confirm('Delete this saved view?')) return;

    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${BACKEND_URL}/api/v1/dashboard/views/${viewId}`, { headers });
      loadViews();
    } catch (error) {
      console.error('Failed to delete view:', error);
      setValidationError('Failed to delete view');
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Save Current View Button */}
      <button
        onClick={() => setShowSaveModal(true)}
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
      >
        <Save className="w-4 h-4" />
        Save View
      </button>

      {/* Saved Views Dropdown */}
      {views.length > 0 && (
        <div className="relative group">
          <button className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition">
            <Eye className="w-4 h-4" />
            Saved Views ({views.length})
          </button>
          <div className="hidden group-hover:block absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-20 min-w-[250px]">
            {views.map((view) => (
              <div
                key={view.id}
                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group/item"
              >
                <button
                  onClick={() => onApplyView(view)}
                  className="flex-1 text-left text-sm"
                >
                  <div className="font-medium text-gray-900">{view.name}</div>
                  {view.description && (
                    <div className="text-xs text-gray-500">{view.description}</div>
                  )}
                </button>
                <button
                  onClick={() => handleDeleteView(view.id)}
                  className="opacity-0 group-hover/item:opacity-100 text-red-600 hover:text-red-800 ml-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save View Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Save Current View</h3>
            <div className="space-y-4">
              {validationError && (
                <p className="text-red-500 text-sm">{validationError}</p>
              )}
              {successMessage && (
                <p className="text-green-600 text-sm">{successMessage}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  View Name *
                </label>
                <input
                  type="text"
                  value={viewName}
                  onChange={(e) => { setViewName(e.target.value); setValidationError(null); }}
                  placeholder="e.g., High Value Claims"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={viewDescription}
                  onChange={(e) => setViewDescription(e.target.value)}
                  placeholder="What does this view show?"
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSaveView}
                  disabled={loading || !viewName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save View'}
                </button>
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setViewName('');
                    setViewDescription('');
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
